import { Component, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { FormArray, FormBuilder, FormControl, FormGroup, ValidatorFn } from '@angular/forms';
import { ActivatedRoute, Data } from '@angular/router';

import { Toaster, Type } from '../toaster/Toaster';
import { Preferences } from '../model/preferences.model';
import { ConfigService } from '../service/config.service';
import { cloneDeep, flatten, isEmpty, isEqual, isInteger } from 'lodash';
import { Keys, StorageService } from '../service/storage.service';
import { PreferencesService } from '../service/preferences.service';
import { PreferredCategories, Rating } from '../model/portfolio-preferences.model';
import { FinzyBoltConsentsComponent } from '../finzy-bolt-consents/finzy-bolt-consents.component';
import { KnowMoreAboutMasterPreferredRatingsComponent } from './know-more-about-master-preferred-ratings/know-more-about-master-preferred-ratings.component';
import { RatingSelectorComponent } from '../portfolio-creation/rating-selector/rating-selector.component';
import { ShowTurnOffSwiftPopupUtil } from '../utils/finzy-flex/show-turn-off-swift-popup.utils';
import { AttributeCode, AttributeValue, InvestmentPreference, UserAttributeInterface } from '../model/finzy-flex/user-attribute.model';
import { ConfirmPopUpFinzyFlexComponent } from '../views/investors/finzy-flex/confirm-pop-up-finzy-flex/confirm-pop-up-finzy-flex.component';
import { ProductList } from '../model/finzy-flex/product-list.model';
@Component({
    providers: [PreferencesService],
    styleUrls: ['./preferences.component.scss'],
    templateUrl: './preferences.component.html'
})
export class PreferencesComponent implements OnInit {
    public preferences: Preferences;
    public savedPreferences: Preferences;
    public userAttributesSwiftForm: FormGroup;
    public backUpUserAttributes: InvestmentPreference[] = []

    private product: ProductList;

    isValid: boolean;

    public shouldShowDataLoader = false;
    public shouldShowSavingLoader = false;
    public loader: { spin: boolean } = { spin: false };
    public shouldShowFinzyProductsAsEntirePrePaymentisActive: boolean;
    public isFinzyProEnabled: boolean;
    public isFinzyFlexEnabled: boolean;
    public userAttributes: InvestmentPreference[] = [];
    private isFinzyFlexProductSelected: boolean = false;
    private isFinzyProProductSelected: boolean = false;
    public FlexData = {
        title: 'Do you want to change',
        subTitle: 'Your current reinvest preference will change',
        message: `You had currently chosen to reinvest into finzyPRO, By clicking on Continue, you are opting to reinvest the selected "EMI" or "Prepayment" Components into finzyFLEX`,
        positiveButtonText: 'Continue',
        negativeButtonText: 'Back',
    }
    public ProData = {
        title: 'Do you want to change',
        subTitle: 'Your current reinvest preference will change',
        message: `You had currently chosen to reinvest into finzyFLEX, By clicking on Continue, you are opting to reinvest the selected "EMI" or "Prepayment" Components into finzyPRO`,
        positiveButtonText: 'Continue',
        negativeButtonText: 'Back',
    }

    @ViewChild(RatingSelectorComponent)
    public ratingSelectorComponent: RatingSelectorComponent;
    public readonly configService: ConfigService;
    private readonly storageService: StorageService;
    private readonly preferencesService: PreferencesService;
    private readonly matDialog: MatDialog;
    private readonly showTurnOffSwiftPopup: ShowTurnOffSwiftPopupUtil;
    isPreviousSwiftAvailable: boolean = true;
    pref: any;

    constructor(activatedRoute: ActivatedRoute, preferencesService: PreferencesService,
        storageService: StorageService, configService: ConfigService,
        matDialogRef: MatDialog, showTurnOffSwiftPopup: ShowTurnOffSwiftPopupUtil,
        formBuilder: FormBuilder) {
        this.configService = configService;
        this.storageService = storageService;
        this.preferencesService = preferencesService;
        this.matDialog = matDialogRef;
        this.showTurnOffSwiftPopup = showTurnOffSwiftPopup;
        this.userAttributesSwiftForm = formBuilder.group({
            fundAllocationPercent: formBuilder.array([])
        });
        
        activatedRoute.data.subscribe((data: Data) => {
            this.product = data.product;
            if(this.product && this.product.data && this.product.data.length) {
                this.product.data = this.product.data.filter((product) => product.code !== 'F18')
            }
            this.preferences = data.investmentPreferences;

            if (!this.preferences) {
                this.preferences = Preferences.createDefault();
            }
            
            if (this.preferences.reinvest.emi.reinvestInterest) {
                this.isFinzyProProductSelected = true;
            }

            if (this.preferences.reinvest.emi.reinvestPrincipal) {
                this.isFinzyProProductSelected = true;
            }

            if (this.preferences.reinvest.prepayment.reinvestInterest || this.preferences.reinvest.prepayment.reinvestPrincipal) {
                this.isFinzyProProductSelected = true;
            }

            if (this.preferences.investmentAttrs.filter(preference => (preference.attributeCode === AttributeCode.FF_REINVEST_PRO && preference.attributeValue === "true")).length > 0) {
                this.isFinzyFlexProductSelected = true;
                this.isFinzyProProductSelected = false;
                this.updateSwiftValidators();
            }
        });
    }

    ngOnInit(): void {
        this.onInit();
    }

    ngAfterViewInit() { 
        this.updateSwiftValidators();
    }

    public async onInit(): Promise<void> {
        this.showDataLoadingLoader();
        try {
            this.savedPreferences = cloneDeep(this.preferences);
            this.backUpUserAttributes = this.preferences.investmentAttrs.filter(att => att.attributeCode === AttributeCode.FF_REINVEST_PRO_FUND_ALLOCATION)
        } catch (error) {
            Toaster.toast('Something went wrong while getting your preferences. Please try again later.', Type.Error);
        } finally {
            this.hideDataLoadingLoader();
        }
    }

    private allocateFundFormValidator(): ValidatorFn {
        return (formArray: FormArray): any => {
            let totalPercent = 0;
            let isFormValid: boolean = true;
            let shouldContainPositiveValue = true;
            formArray.controls.forEach((element: FormControl) => {
                if (element.value.attributeValue < 0 && element.value.attributeValue !== '' && element.value.attributeValue !== null) {
                    shouldContainPositiveValue = false;
                }
                totalPercent = totalPercent + element.value.attributeValue;
            });
            if (!shouldContainPositiveValue) {
                isFormValid = false;
                return isFormValid ? null : { error: 'negativeValueValidatorError' }
            } else {
                isFormValid = totalPercent == 100 ? true : false;
                return isFormValid ? null : { error: 'allocateFundFormValidatorError' };
            }
        };
    }

    public isFirstReinvestment(value: Object): boolean {
        return Object.values(value).every(v => v && typeof v === 'object'
            ? this.isFirstReinvestment(v)
            : v === false
        );
    }

    public onReinvestEMIPrincipalPreferenceChange(value: boolean): void {
        this.preferences.reinvest.emi.reinvestPrincipal = value;
        if (this.isFirstReinvestment(this.savedPreferences.reinvest)) {
            this.isFinzyFlexProductSelected = true
            this.isFinzyProProductSelected = false;
            this.updateSwiftValidators();
        }
    }

    public onReinvestEMIInterestPreferenceChange(value: boolean): void {
        this.preferences.reinvest.emi.reinvestInterest = value;
        if (this.isFirstReinvestment(this.savedPreferences.reinvest)) {
            this.isFinzyFlexProductSelected = true;
            this.isFinzyProProductSelected = false;
            this.updateSwiftValidators();
        }
    }

    public onReinvestPrepaymentPreferenceChange(value: boolean): void {
        this.preferences.reinvest.prepayment.reinvestInterest = value;
        this.preferences.reinvest.prepayment.reinvestPrincipal = value;
        this.shouldShowFinzyProductsAsEntirePrePaymentisActive = value;
        if (this.isFirstReinvestment(this.savedPreferences.reinvest)) {
            this.isFinzyFlexProductSelected = true;
            this.isFinzyProProductSelected = false;
            this.updateSwiftValidators();
        }
    }

    public async onClickReinvestInFinzyPROProduct(value: boolean) {
        this.isFinzyProProductSelected = value;
        this.isFinzyFlexProductSelected = !value;
        if (this.isFinzyFlexProductSelected) {
            this.updateSwiftValidators();
            await this.dialogHandler(this.FlexData)
        }
        if (this.isFinzyProProductSelected) {
            this.userAttributesSwiftForm.reset();
            await this.dialogHandler(this.ProData);
        }
    }

    private async dialogHandler(dialogData: object) {
        await this.onOpenConfirmationPopup({
            data: dialogData,
            width: '60%',
            minWidth: 'fit-content',
            panelClass: 'disable-preference-popup'
        });
    }

    private updateSwiftValidators() {
        if (this.isFinzyFlexProductSelected) {
            this.userAttributesSwiftForm.controls.fundAllocationPercent.setValidators([this.allocateFundFormValidator()]);
            this.userAttributesSwiftForm.controls.fundAllocationPercent.updateValueAndValidity();
        } else {
            this.userAttributesSwiftForm.controls.fundAllocationPercent.clearValidators();
            this.userAttributesSwiftForm.controls.fundAllocationPercent.updateValueAndValidity();
        }
    }


    public async onClickReinvestInFinzyFLEXProduct(value: boolean) {
        this.userAttributesSwiftForm.controls.fundAllocationPercent.setValidators([this.allocateFundFormValidator()]);
        this.isFinzyFlexProductSelected = value;
        this.isFinzyProProductSelected = !value;
        if (this.isFinzyFlexProductSelected) {
            this.updateSwiftValidators();
            await this.dialogHandler(this.FlexData);
        }
        if (this.isFinzyProProductSelected) {
            await this.dialogHandler(this.ProData);
        }
    }


    public onAutomaticInvestmentsFromWalletPreferenceChange(value: boolean): void {
        this.preferences.automaticInvestmentsFromWallet = value;
    }

    public onAutoApproveLoanInvestmentChange(value: boolean): void {
        this.preferences.allocatedLoanNotification = value;
    }

    public async onSavePreferencesClicked(preferences: Preferences): Promise<void> {

        // Current user preferences coming from form
        let reinvestAllocation: Array<any> = this.userAttributesSwiftForm.value.fundAllocationPercent
            .filter((val) => Boolean(val.attributeValue))
            .map((val) => ({ ...val, 
                attributeCode: AttributeCode.FF_REINVEST_PRO_FUND_ALLOCATION,
                attributeValue: isInteger(val.attributeValue) ? (val.attributeValue).toString() : val.attributeValue
            }));

        // Checking is userAttributesSwiftForm has value and  isFinzyFlexProductSelected selected
        if ((reinvestAllocation.length > 0) && this.isFinzyFlexProductSelected && this.userAttributesSwiftForm.valid) {
            if(this.isInvestmentPreferenceSumHundredPer(reinvestAllocation)) {
                reinvestAllocation.push({
                    "attributeCode": AttributeCode.FF_REINVEST_PRO,
                    "attributeValue": "true",
                    "productCode": null,
                    "productVersion": null
                })
            } else {
                Toaster.toast('Allocation must add upto 100% in one or multiple investment options', Type.Error);
            }
        } else if(this.isFinzyFlexProductSelected && this.userAttributesSwiftForm.invalid) {
            Toaster.toast('Allocation must add upto 100% in one or multiple investment options', Type.Error);
            return;
        } else {
            reinvestAllocation = [];
            this.backUpUserAttributes = [];
        }

        // Old user preferences came from api and removing them to add new preferences
        preferences.investmentAttrs = preferences.investmentAttrs
            .filter((val) => ((val.attributeCode !== AttributeCode.FF_REINVEST_PRO_FUND_ALLOCATION) && (val.attributeCode !== AttributeCode.FF_REINVEST_PRO)))

        if (reinvestAllocation.length) {
            preferences.investmentAttrs = preferences.investmentAttrs.concat(reinvestAllocation)
        }

        if (this.showTurnOffSwiftPopup.isSwiftEnabled(this.savedPreferences) && preferences.automaticInvestmentsFromWallet) {
            try {
                const response: UserAttributeInterface = await this.showTurnOffSwiftPopup.showFinzySwiftPopup(this.savedPreferences, this.loader, this.storageService.getItem(Keys.PROFILE_ID));
                this.loader.spin = false;
                if (response.status === 0) {
                    this.preferences.investmentAttrs = response.data[0].investmentPreferences;
                    this.savedPreferences.investmentAttrs = response.data[0].investmentPreferences;
                    Toaster.toast('Your swift preference switched off successfully!', Type.Success);
                    if (!this.savedPreferences.automaticInvestmentsFromWallet) {
                        await this.requestForConsents(preferences);
                    } else {
                        await this.savePreferences(preferences);
                    }
                }
                else if (response.status === 1) {
                    Toaster.toast('Failed to update preference. Please try again after sometime.', Type.Error);
                }
            } catch (error) {
                Toaster.toast('Please try again', Type.Error);
                this.loader.spin = false;
            }
        }
        else {
            if (preferences.automaticInvestmentsFromWallet && !this.savedPreferences.automaticInvestmentsFromWallet) {
                await this.requestForConsents(preferences);
            } else {
                await this.savePreferences(preferences);
            }
        }
    }

    public isInvestmentPreferenceSumHundredPer(reinvestAllocation: InvestmentPreference[]) {
        const isSumHundredPer =  reinvestAllocation.reduce((preVal: number, curVal) => preVal + parseInt(curVal.attributeValue),0) === 100;
        return isSumHundredPer;
    }

    public async requestForConsents(preferences: Preferences): Promise<void> {
        this.matDialog.open(FinzyBoltConsentsComponent, {
            autoFocus: false
        }).afterClosed().subscribe(areConsentsTaken => {
            if (areConsentsTaken) {
                this.savePreferences(preferences);
            }
        });
    }

    public async savePreferences(preferences: Preferences) {
        try {
            this.showSavingLoader();
            let preferencesPayload: any = {};
            preferencesPayload = { ...preferences };
            preferencesPayload['investmentPreferences'] = preferencesPayload.investmentAttrs;
            delete preferencesPayload['investmentAttrs'];
            const userId = this.storageService.getItem(Keys.PROFILE_ID);
            const savedPreferences = await this.preferencesService.saveUserPreferences(userId, preferencesPayload, 'true');
            Toaster.toast('Your preferences have been updated successfully!', Type.Success);

            this.preferences = savedPreferences;
            this.savedPreferences = cloneDeep(savedPreferences);
        } catch (error) {
            Toaster.toast('Something went wrong while saving your preferences. Please try again later.', Type.Error);
        } finally {
            this.hideSavingLoader();
        }
    }

    public showSavingLoader(): void {
        this.shouldShowSavingLoader = true;
    }

    public hideSavingLoader(): void {
        this.shouldShowSavingLoader = false;
    }

    public showDataLoadingLoader(): void {
        this.shouldShowDataLoader = true;
    }

    public hideDataLoadingLoader(): void {
        this.shouldShowDataLoader = false;
    }

    public shouldStopFromNavigatingAway(): boolean {
        return this.havePreferencesChanged();
    }

    public havePreferencesChanged() {

        // INFO: This step is just to validate json object inside  Array
        const savedPreferences: any = {...this.savedPreferences};
        savedPreferences.investmentAttrs = new Set(savedPreferences.investmentAttrs);

        const preferences: any = {...this.preferences};
        preferences.investmentAttrs = new Set(preferences.investmentAttrs);

        return !isEqual(savedPreferences, preferences);
    }

    public shouldDisableSaveButton() {
        const onlyForbiddenRatings = this.containsOnlyForbiddenRatings(this.preferences.preferredRatings);
        const isDisabledSaveButton = !this.havePreferencesChanged() || isEmpty(this.preferences.preferredRatings) || onlyForbiddenRatings || this.preferences.preferredRatings.size < 12;
        if (isDisabledSaveButton) {
            return true;
        }
        return false;
    }

    public containsOnlyForbiddenRatings(preferredRatings: Set<Rating>) {
        if (!this.ratingSelectorComponent) {
            return true;
        }
        return this.ratingSelectorComponent.containsOnlyForbiddenRatings([{ ratingList: Array.from(preferredRatings) }]);
    }

    public onPreferredCategoriesChanged(categories: PreferredCategories[]) {
        this.preferences.preferredRatings = new Set<Rating>(flatten(categories.map(category => category.ratingList)));
    }

    public onPreferredLoanTenureChanged(preference: Preferences) {
        this.preferences.investmentPreferences = preference.investmentPreferences;
    }

    public onPreferredMaxLoanAmountChanged(preference: Preferences) {
        this.preferences.investmentPreferences = preference.investmentPreferences;
    }

    public convertRatingsToPreferredCategories(preferredRatings: Set<Rating>) {
        return [{ ratingList: Array.from(preferredRatings) }];
    }

    public onKnowMoreAboutFinzyProMasterPreferencesClicked() {
        this.matDialog.open(KnowMoreAboutMasterPreferredRatingsComponent);
    }

    private async onOpenConfirmationPopup(data): Promise<any> {
        return this.matDialog.open(ConfirmPopUpFinzyFlexComponent, data).afterClosed().subscribe(
            result => {
                if (!result && !this.isFinzyProProductSelected) {
                    this.isFinzyProProductSelected = true;
                    this.isFinzyFlexProductSelected = false;
                } else if (result && this.isFinzyProProductSelected) {
                    this.isFinzyProProductSelected = true;
                    this.isFinzyFlexProductSelected = false;
                    this.preferences.investmentAttrs = this.preferences.investmentAttrs.filter((val) => {
                        if((val.attributeCode !== AttributeCode.FF_REINVEST_PRO) && (val.attributeValue !== "true")) {
                            return true;
                        }
                    });
                } else if (result && !this.isFinzyProProductSelected) {
                    this.isFinzyProProductSelected = false;
                    this.isFinzyFlexProductSelected = true;
                    this.preferences.investmentAttrs = this.preferences.investmentAttrs.filter((val) => {
                        if((val.attributeCode === AttributeCode.FF_REINVEST_PRO) && (val.attributeValue === "false")) {
                            return false;
                        }
                        return true;
                    });
                    this.preferences.investmentAttrs.push({ 
                        "attributeCode": AttributeCode.FF_REINVEST_PRO, 
                        "attributeValue": "true",
                        "productCode": null,
                        "productVersion": null 
                    });
                } else if (!result && !this.isFinzyProProductSelected) {
                    this.isFinzyProProductSelected = true;
                    this.isFinzyFlexProductSelected = false;
                } else if (!result && !this.isFinzyFlexProductSelected) {
                    this.isFinzyProProductSelected = false;
                    this.isFinzyFlexProductSelected = true;
                } else if (result && !this.isFinzyFlexProductSelected) {
                    this.isFinzyProProductSelected = true;
                    this.isFinzyFlexProductSelected = false;
                } else {
                    this.isFinzyProProductSelected = true;
                    this.isFinzyFlexProductSelected = false;
                }
            }
        );
    }

    public isShowSelectProductsToReinvest() {
        if (this.preferences?.reinvest?.emi?.reinvestPrincipal || this.preferences?.reinvest?.emi?.reinvestInterest || this.preferences?.reinvest?.prepayment?.reinvestPrincipal) {
            return true;
        };
        return false;
    }

    public getUserAttributesSwiftFormValueOnChange(event: InvestmentPreference[]) {
        this.preferences.investmentAttrs = this.preferences.investmentAttrs.filter((val) => val.attributeCode !== AttributeCode.FF_REINVEST_PRO_FUND_ALLOCATION);
        this.preferences.investmentAttrs = this.preferences.investmentAttrs.concat(event.filter((val) =>  val.attributeValue !== null));
    }
}
